import { getAccessToken } from "./authService";
import { sendDeviceToken } from "../services/auth.api";
import type {
  PushPlatform,
  PushTokenFields,
} from "../services/auth.type";

/**
 * Centralized handling of the FCM push token injected by the native Flutter
 * WebView wrapper.
 *
 * Flutter dispatches a `nativePushToken` CustomEvent into the page after
 * `onPageFinished` and again on every token refresh. The payload looks like:
 *
 *   { type: "pushToken", token: "<fcm>", platform: "ios"|"android", ts: number }
 *
 * In a normal browser the event never fires, so all getters return empty values
 * and auth requests simply omit the push fields.
 */

const STORAGE_KEY = "pushToken";
const NATIVE_EVENT = "nativePushToken";
const VALID_PLATFORMS: readonly PushPlatform[] = ["ios", "android"];

interface NativePushTokenPayload {
  type?: string;
  token?: unknown;
  platform?: unknown;
  ts?: unknown;
}

interface CachedPushToken {
  token: string;
  platform: PushPlatform;
  ts?: number;
}

/** In-memory copy of the latest token; survives until a full page reload. */
let cachedToken: CachedPushToken | null = null;
let listenerInitialized = false;

const isPlatform = (value: unknown): value is PushPlatform =>
  typeof value === "string" &&
  VALID_PLATFORMS.includes(value as PushPlatform);

const isValidPayload = (
  payload: NativePushTokenPayload | null | undefined,
): payload is { token: string; platform: PushPlatform; ts?: number } => {
  if (!payload) return false;
  const tokenValid = typeof payload.token === "string" && payload.token.trim().length > 0;
  return tokenValid && isPlatform(payload.platform);
};

const readFromStorage = (): CachedPushToken | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NativePushTokenPayload;
    if (!isValidPayload(parsed)) return null;
    return {
      token: parsed.token,
      platform: parsed.platform,
      ts: typeof parsed.ts === "number" ? parsed.ts : undefined,
    };
  } catch {
    return null;
  }
};

const writeToStorage = (value: CachedPushToken): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable (private mode / quota); ignore.
  }
};

/** Latest cached token + platform, or `null` outside the WebView. */
export const getCachedPushToken = (): CachedPushToken | null => {
  if (cachedToken) return cachedToken;
  cachedToken = readFromStorage();
  return cachedToken;
};

/**
 * Returns the push fields to spread into auth request bodies. Returns an empty
 * object when no token is cached, so requests omit the fields gracefully.
 */
export const getPushTokenFields = (): Partial<PushTokenFields> => {
  const cached = getCachedPushToken();
  if (!cached) return {};
  return { fcm_token: cached.token, platform: cached.platform };
};

/**
 * Validates and caches the latest token in memory + localStorage.
 * Returns the cached value, or `null` when the payload is invalid.
 */
export const setPushToken = (
  payload: NativePushTokenPayload | null | undefined,
): CachedPushToken | null => {
  if (!isValidPayload(payload)) return null;
  cachedToken = {
    token: payload.token.trim(),
    platform: payload.platform,
    ts: typeof payload.ts === "number" ? payload.ts : undefined,
  };
  writeToStorage(cachedToken);
  return cachedToken;
};

/**
 * Registers the cached device token with the backend. No-op when there is no
 * cached token (normal browser) or no authenticated session.
 */
export const registerDeviceToken = async (): Promise<void> => {
  const fields = getPushTokenFields();
  if (!fields.fcm_token || !fields.platform) return;
  if (!getAccessToken()) return;
  try {
    await sendDeviceToken({
      fcm_token: fields.fcm_token,
      platform: fields.platform,
    });
  } catch (error) {
    console.error("Failed to register device token:", error);
  }
};

const handleNativePushToken = (event: Event): void => {
  // Flutter dispatches a CustomEvent whose `detail` carries the payload, but
  // fall back to the event object itself in case fields are attached directly.
  const detail =
    (event as CustomEvent<NativePushTokenPayload>).detail ??
    (event as unknown as NativePushTokenPayload);
  const updated = setPushToken(detail);
  if (!updated) return;
  // Token-refresh while signed in: push the new token to the backend.
  void registerDeviceToken();
};

/**
 * Starts listening for the native `nativePushToken` event. Safe to call once at
 * app startup; subsequent calls are ignored.
 */
export const initPushTokenListener = (): void => {
  if (listenerInitialized || typeof window === "undefined") return;
  listenerInitialized = true;
  // Prime the in-memory cache from a previous session.
  getCachedPushToken();
  window.addEventListener(NATIVE_EVENT, handleNativePushToken as EventListener);
};
